import React from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { nestedSurfaceSx, statusTone, toneMain } from "./channelTheme";
import { ChannelStatusChip, ChannelSparkline, TankFill, DeltaText } from "./ChannelVisuals";
import { formatChannelValue, needsAttention } from "./channelUtils";

const tileSx = (channel, selected) => (theme) => {
  const { palette, functions: { rgba } } = theme;
  const tone = toneMain(palette, statusTone(channel));
  return {
    ...nestedSurfaceSx(theme),
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    textAlign: "left",
    width: "100%",
    height: "100%",
    gap: 1,
    p: 1.5,
    borderLeft: `3px solid ${needsAttention(channel) ? tone : rgba(palette.text.main, 0.1)}`,
    transition: "border-color 150ms ease, background-color 150ms ease",
    ...(selected && {
      borderColor: palette.info.main,
      borderLeftColor: needsAttention(channel) ? tone : palette.info.main,
      backgroundColor: rgba(palette.info.main, 0.1),
    }),
    "&:hover": { backgroundColor: rgba(palette.text.main, selected ? 0.1 : 0.07) },
    "&.Mui-focusVisible": { outline: `2px solid ${palette.info.main}`, outlineOffset: 2 },
  };
};

const updatedText = (ts) => {
  if (!ts) return null;
  const mins = Math.round((Date.now() - ts) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)} h ago`;
  return `${Math.round(mins / 1440)} d ago`;
};

function TileHeader({ channel }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
      <Icon
        className={channel.icon}
        sx={({ palette }) => ({ fontSize: "0.95rem !important", width: 18, color: toneMain(palette, "info") })}
      />
      <MDTypography variant="button" fontWeight="bold" noWrap title={channel.name} sx={{ flex: 1, minWidth: 0 }}>
        {channel.name}
      </MDTypography>
      <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
        #{channel.number}
      </MDTypography>
    </MDBox>
  );
}

export function ChannelTile({ channel, selected, density = "cards", onSelect }) {
  const compact = density === "compact";
  const isLevel = channel.kind === "level";
  return (
    <ButtonBase
      focusRipple
      onClick={() => onSelect?.(channel.number)}
      aria-pressed={!!selected}
      aria-label={`${channel.name}, ${formatChannelValue(channel.value, channel.unit, channel.kind)}, ${channel.status.label}`}
      sx={tileSx(channel, selected)}
    >
      <TileHeader channel={channel} />

      <MDBox display="flex" alignItems="baseline" justifyContent="space-between" gap={1}>
        <MDTypography variant={compact ? "h6" : "h4"} fontWeight="bold" lineHeight={1.1}>
          {formatChannelValue(channel.value, channel.unit, channel.kind)}
        </MDTypography>
        {compact ? (
          <ChannelStatusChip channel={channel} />
        ) : (
          isLevel &&
          channel.volume !== null && (
            <MDTypography variant="caption" color="text" noWrap>
              {formatChannelValue(channel.volume, channel.volumeUnit)}
            </MDTypography>
          )
        )}
      </MDBox>

      {isLevel && <TankFill channel={channel} height={compact ? 6 : 8} />}

      {!compact && (
        <>
          <ChannelSparkline points={channel.points24} tone={channel.status.level === "ok" ? "info" : statusTone(channel)} />
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <ChannelStatusChip channel={channel} />
            <MDBox display="flex" flexDirection="column" alignItems="flex-end">
              <DeltaText delta={channel.delta24} unit={channel.unit} />
              {channel.updatedAt && (
                <MDTypography variant="caption" color="text" sx={{ opacity: 0.6, fontSize: "0.65rem" }}>
                  {updatedText(channel.updatedAt)}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </>
      )}
    </ButtonBase>
  );
}

// Temperature + humidity sensors that share a name stem ("Freezer 1 Temp" / "Freezer 1 Humidity").
export function ZoneTile({ zone, selectedKey, density = "cards", onSelect }) {
  const compact = density === "compact";
  const worst = zone.members.reduce(
    (w, c) => (needsAttention(c) && (!w || c.status.level === "alarm") ? c : w),
    null
  );
  return (
    <MDBox
      sx={(theme) => ({
        ...nestedSurfaceSx(theme),
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        height: "100%",
        borderLeft: `3px solid ${
          worst ? toneMain(theme.palette, statusTone(worst)) : theme.functions.rgba(theme.palette.text.main, 0.1)
        }`,
      })}
    >
      <MDBox display="flex" alignItems="center" gap={1}>
        <Icon className="fa-solid fa-layer-group" sx={({ palette }) => ({ fontSize: "0.9rem !important", color: palette.info.main })} />
        <MDTypography variant="button" fontWeight="bold" noWrap sx={{ flex: 1 }}>
          {zone.label}
        </MDTypography>
        {worst && <ChannelStatusChip channel={worst} />}
      </MDBox>
      <MDBox display="grid" gridTemplateColumns={`repeat(${Math.min(zone.members.length, 2)}, 1fr)`} gap={1}>
        {zone.members.map((c) => {
          const selected = String(selectedKey) === c.key;
          return (
            <Tooltip key={c.key} title={`${c.name} · #${c.number} · ${c.status.label}`} placement="top">
              <ButtonBase
                focusRipple
                onClick={() => onSelect?.(c.number)}
                aria-pressed={selected}
                sx={({ palette, borders, functions: { rgba } }) => ({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  p: 1,
                  borderRadius: borders.borderRadius.md,
                  border: `1px solid ${selected ? palette.info.main : rgba(palette.text.main, 0.08)}`,
                  backgroundColor: selected ? rgba(palette.info.main, 0.12) : "transparent",
                  "&:hover": { backgroundColor: rgba(palette.text.main, 0.06) },
                  "&.Mui-focusVisible": { outline: `2px solid ${palette.info.main}`, outlineOffset: 2 },
                })}
              >
                <MDTypography variant="caption" color="text" noWrap>
                  <Icon className={c.icon} sx={{ fontSize: "0.7rem !important", mr: 0.5, verticalAlign: "middle" }} />
                  {c.kindLabel}
                </MDTypography>
                <MDTypography
                  variant={compact ? "button" : "h5"}
                  fontWeight="bold"
                  sx={({ palette }) => ({
                    color: needsAttention(c) ? toneMain(palette, statusTone(c)) : undefined,
                  })}
                >
                  {formatChannelValue(c.value, c.unit, c.kind)}
                </MDTypography>
                {!compact && <DeltaText delta={c.delta24} unit={c.unit} />}
              </ButtonBase>
            </Tooltip>
          );
        })}
      </MDBox>
    </MDBox>
  );
}
